import { Lock, Mail } from "@mui/icons-material";
import SendIcon from "@mui/icons-material/Send";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import InputAdornment from "@mui/material/InputAdornment";
import Link from "@mui/material/Link";
import { styled } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import React, { useContext, useState } from "react";
import { Helmet } from "react-helmet";
import logo from "../../assets/logo.png";
import { AuthContext } from "../../context/Auth/AuthContext";
import ColorModeContext from "../../layout/themeContext";

// Container principal com layout split
const LoginContainer = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
  display: "flex",
  position: "relative",
  overflow: "hidden",
  backgroundColor: theme.palette.mode === "dark" ? "#0F172A" : "#F8FAFC",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },
}));

// Lado esquerdo com background animado
const LeftSection = styled(Box)(({ theme }) => ({
  flex: "0 0 60%",
  position: "relative",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  padding: theme.spacing(4),
  background: `linear-gradient(135deg, ${theme.palette.mode === "dark" ? "#1E293B" : "#0F172A"
    } 0%, ${theme.palette.mode === "dark" ? "#0F172A" : "#1E293B"} 50%, ${theme.palette.mode === "dark" ? "#35af9a" : "#2d9a87"
    } 100%)`,
  backgroundSize: "400% 400%",
  animation: "gradientShift 15s ease infinite",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: "-50%",
    left: "-50%",
    width: "200%",
    height: "200%",
    background: `radial-gradient(circle, ${theme.palette.mode === "dark" ? "rgba(53, 175, 154, 0.1)" : "rgba(53, 175, 154, 0.15)"
      } 0%, transparent 70%)`,
    animation: "rotate 20s linear infinite",
  },
  "&::after": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
    opacity: 0.3,
  },
  [theme.breakpoints.down("md")]: {
    flex: "0 0 40%",
    minHeight: "40vh",
  },
  "@keyframes gradientShift": {
    "0%": {
      backgroundPosition: "0% 50%",
    },
    "50%": {
      backgroundPosition: "100% 50%",
    },
    "100%": {
      backgroundPosition: "0% 50%",
    },
  },
  "@keyframes rotate": {
    "0%": {
      transform: "rotate(0deg)",
    },
    "100%": {
      transform: "rotate(360deg)",
    },
  },
}));

// Elementos decorativos flutuantes
const FloatingCircle = styled(Box)(({ theme, size, left, top, delay }) => ({
  position: "absolute",
  width: size || "200px",
  height: size || "200px",
  borderRadius: "50%",
  background: `radial-gradient(circle, ${theme.palette.mode === "dark" ? "rgba(53, 175, 154, 0.2)" : "rgba(53, 175, 154, 0.25)"
    } 0%, transparent 70%)`,
  left: left || "10%",
  top: top || "20%",
  animation: `float ${6 + Math.random() * 4}s ease-in-out infinite`,
  animationDelay: `${delay || 0}s`,
  filter: "blur(40px)",
  "@keyframes float": {
    "0%, 100%": {
      transform: "translateY(0px) scale(1)",
      opacity: 0.6,
    },
    "50%": {
      transform: `translateY(-${20 + Math.random() * 30}px) scale(${1 + Math.random() * 0.2})`,
      opacity: 0.8,
    },
  },
}));

// Conteúdo do lado esquerdo
const LeftContent = styled(Box)(({ theme }) => ({
  position: "relative",
  zIndex: 2,
  textAlign: "center",
  color: "#FFFFFF",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.spacing(3),
}));

// Lado direito com formulário de login
const RightSection = styled(Box)(({ theme }) => ({
  flex: "0 0 40%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  padding: theme.spacing(6),
  backgroundColor:
    theme.palette.mode === "dark" ? "#0F172A" : "#F8FAFC",
  position: "relative",
  width: "100%",
  maxWidth: "600px",
  margin: "0 auto",
  [theme.breakpoints.down("md")]: {
    flex: "0 0 60%",
    minHeight: "60vh",
    maxWidth: "100%",
    padding: theme.spacing(4),
  },
}));

// TextField estilizado
const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255, 255, 255, 0.05)"
        : "#F8FAFC",
    fontFamily: '"Inter", "Poppins", sans-serif',
    "& fieldset": {
      borderColor:
        theme.palette.mode === "dark"
          ? "rgba(255, 255, 255, 0.12)"
          : "#E2E8F0",
      borderWidth: "1px",
    },
    "&:hover fieldset": {
      borderColor:
        theme.palette.mode === "dark"
          ? "rgba(255, 255, 255, 0.2)"
          : "#CBD5E1",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#35af9a",
      borderWidth: "1.5px",
    },
  },
  "& .MuiInputBase-input": {
    color: theme.palette.mode === "dark" ? "#F1F5F9" : "#1E293B",
    paddingLeft: "10px",
    fontFamily: '"Inter", "Poppins", sans-serif',
    fontSize: "14px",
  },
  "& .MuiInputLabel-root": {
    color:
      theme.palette.mode === "dark" ? "#94A3B8" : "#64748B",
    fontFamily: '"Inter", "Poppins", sans-serif',
    fontSize: "14px",
  },
  "& .MuiFormHelperText-root": {
    fontFamily: '"Inter", "Poppins", sans-serif',
    fontSize: "12px",
  },
}));

// FormLabel estilizado
const StyledFormLabel = styled(FormLabel)(({ theme }) => ({
  color: theme.palette.mode === "dark" ? "#94A3B8" : "#64748B",
  marginBottom: "8px",
  fontFamily: '"Inter", "Poppins", sans-serif',
  fontSize: "14px",
  fontWeight: 500,
}));

// Link estilizado
const StyledLink = styled(Link)(({ theme }) => ({
  color: theme.palette.mode === "dark" ? "#94A3B8" : "#64748B",
  fontFamily: '"Inter", "Poppins", sans-serif',
  fontSize: "14px",
  textDecoration: "none",
  "&:hover": {
    color: "#35af9a",
    textDecoration: "underline",
  },
}));

const Login = () => {
  const theme = useTheme();
  const [emailError, setEmailError] = useState(false);
  const [emailErrorMessage, setEmailErrorMessage] = useState("");
  const [passwordError, setPasswordError] = useState(false);
  const [passwordErrorMessage, setPasswordErrorMessage] = useState("");
  const { colorMode } = useContext(ColorModeContext);
  const { appName } = colorMode;
  const [user, setUser] = useState({ email: "", password: "" });
  const { handleLogin } = useContext(AuthContext);

  const handleChangeInput = (e) => {
    setUser({ ...user, [e.target.name]: e.target.value });
  };

  const handlSubmit = (e) => {
    e.preventDefault();
    handleLogin(user);
  };

  const validateInputs = () => {
    const email = document.getElementById("email");
    const password = document.getElementById("password");
    let isValid = true;

    if (!email.value || !/\S+@\S+\.\S+/.test(email.value)) {
      setEmailError(true);
      setEmailErrorMessage("Por favor, insira um email válido.");
      isValid = false;
    } else {
      setEmailError(false);
      setEmailErrorMessage("");
    }

    if (!password.value || password.value.length < 6) {
      setPasswordError(true);
      setPasswordErrorMessage("A senha deve ter pelo menos 6 caracteres.");
      isValid = false;
    } else {
      setPasswordError(false);
      setPasswordErrorMessage("");
    }

    return isValid;
  };

  return (
    <>
      <Helmet>
        <title>{appName || "WHATTIS"}</title>
        <link rel="icon" href="/favicon.png" />
      </Helmet>
      <CssBaseline enableColorScheme />
      <LoginContainer>
        {/* Lado esquerdo com background animado */}
        <LeftSection>
          {/* Elementos decorativos flutuantes */}
          <FloatingCircle size="300px" left="5%" top="10%" delay="0" />
          <FloatingCircle size="200px" left="70%" top="60%" delay="1" />
          <FloatingCircle size="250px" left="50%" top="30%" delay="2" />

          <LeftContent>
            <Box
              sx={{
                mb: 2,
              }}
            >
              <img
                src={logo}
                alt="Logo"
                style={{
                  width: "280px",
                  maxWidth: "100%",
                  filter: "drop-shadow(0 4px 20px rgba(0, 0, 0, 0.3))",
                }}
              />
            </Box>
            <Typography
              variant="h3"
              sx={{
                color: "#FFFFFF",
                fontWeight: 700,
                fontFamily: '"Inter", "Poppins", sans-serif',
                fontSize: { xs: "1.75rem", md: "2.5rem" },
                textShadow: "0 2px 10px rgba(0, 0, 0, 0.2)",
              }}
            >
              Bem-vindo de volta!
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "rgba(255, 255, 255, 0.9)",
                fontFamily: '"Inter", "Poppins", sans-serif',
                fontSize: { xs: "0.9rem", md: "1rem" },
                maxWidth: "400px",
                textShadow: "0 1px 5px rgba(0, 0, 0, 0.2)",
              }}
            >
              Acesse sua conta para continuar usando o sistema
            </Typography>
          </LeftContent>
        </LeftSection>

        {/* Lado direito com formulário de login */}
        <RightSection>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mb: 4,
              gap: 1.5,
              width: "100%",
            }}
          >
            <Typography
              variant="h4"
              sx={{
                color:
                  theme.palette.mode === "dark" ? "#F1F5F9" : "#1E293B",
                fontWeight: 700,
                fontFamily: '"Inter", "Poppins", sans-serif',
                fontSize: { xs: "1.75rem", md: "2rem" },
              }}
            >
              Fazer Login
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color:
                  theme.palette.mode === "dark" ? "#94A3B8" : "#64748B",
                fontFamily: '"Inter", "Poppins", sans-serif',
                fontSize: "1rem",
              }}
            >
              Entre com suas credenciais
            </Typography>
          </Box>

          <Box
            component="form"
            onSubmit={handlSubmit}
            noValidate
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              gap: 3.5,
            }}
          >
            <FormControl fullWidth>
              <StyledFormLabel htmlFor="email">E-mail</StyledFormLabel>
              <StyledTextField
                error={emailError}
                helperText={emailErrorMessage}
                id="email"
                type="email"
                name="email"
                placeholder="seu@email.com"
                autoComplete="email"
                autoFocus
                required
                fullWidth
                onChange={handleChangeInput}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Mail
                        sx={{
                          color:
                            theme.palette.mode === "dark"
                              ? "#94A3B8"
                              : "#64748B",
                        }}
                      />
                    </InputAdornment>
                  ),
                }}
              />
            </FormControl>

            <FormControl fullWidth>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <StyledFormLabel htmlFor="password">Senha</StyledFormLabel>
                <StyledLink href="/recovery-password" variant="body2">
                  Esqueci minha senha
                </StyledLink>
              </Box>
              <StyledTextField
                error={passwordError}
                helperText={passwordErrorMessage}
                name="password"
                placeholder="••••••••"
                type="password"
                id="password"
                autoComplete="current-password"
                required
                fullWidth
                onChange={handleChangeInput}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock
                        sx={{
                          color:
                            theme.palette.mode === "dark"
                              ? "#94A3B8"
                              : "#64748B",
                        }}
                      />
                    </InputAdornment>
                  ),
                }}
              />
            </FormControl>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              onClick={validateInputs}
              sx={{
                borderRadius: "12px",
                padding: "12px 24px",
                background: "#35af9a",
                color: "#FFFFFF",
                textTransform: "none",
                fontSize: "1rem",
                fontWeight: 600,
                fontFamily: '"Inter", "Poppins", sans-serif',
                boxShadow: "0 4px 12px rgba(53, 175, 154, 0.3)",
                transition: "all 0.3s ease",
                "&:hover": {
                  background: "#2d9a87",
                  boxShadow: "0 6px 16px rgba(53, 175, 154, 0.4)",
                  transform: "translateY(-1px)",
                },
                "&:active": {
                  transform: "translateY(0px)",
                },
              }}
              endIcon={<SendIcon />}
            >
              Entrar
            </Button>
          </Box>
        </RightSection>
      </LoginContainer>
    </>
  );
};

export default Login;
